import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Eye, Plus, Download, Search } from 'lucide-react';

interface Complaint {
  id: string;
  title: string;
  description: string;
  machine: string;
  currentCounter: number;
  agent: string;
  status: 'nouveau' | 'en_cours' | 'resolue';
  createdAt: string;
  updatedAt: string;
}

interface ComplaintsListProps {
  onCreateNew?: () => void;
  onViewDetail?: (complaint: Complaint) => void;
}

export const ComplaintsList = ({ onCreateNew, onViewDetail }: ComplaintsListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Mock data - in real app this would come from API
  const complaints: Complaint[] = [
    {
      id: 'REC001',
      title: 'Problème de qualité d\'impression',
      description: 'Les impressions sortent floues',
      machine: 'HP LaserJet Pro MFP M428fdw',
      currentCounter: 15420,
      agent: 'Mohammed Alami',
      status: 'nouveau',
      createdAt: '2024-01-15',
      updatedAt: '2024-01-15'
    },
    {
      id: 'REC002',
      title: 'Bourrage papier fréquent',
      description: 'La machine se bloque tous les jours',
      machine: 'Canon imageRUNNER ADVANCE C3330i',
      currentCounter: 8930,
      agent: 'Fatima Bennani',
      status: 'en_cours',
      createdAt: '2024-01-10',
      updatedAt: '2024-01-12'
    },
    {
      id: 'REC003',
      title: 'Toner défectueux',
      description: 'Le toner neuf ne fonctionne pas correctement',
      machine: 'Xerox WorkCentre 6515',
      currentCounter: 22150,
      agent: 'Ahmed Kadiri',
      status: 'resolue',
      createdAt: '2024-01-05',
      updatedAt: '2024-01-08'
    },
    {
      id: 'REC004',
      title: 'Problème de connexion réseau',
      description: 'Impossible d\'imprimer depuis le réseau',
      machine: 'Brother MFC-L8900CDW',
      currentCounter: 12500,
      agent: 'Rachid Benjelloun',
      status: 'en_cours',
      createdAt: '2024-01-18',
      updatedAt: '2024-01-20'
    },
    {
      id: 'REC005',
      title: 'Écran tactile ne répond pas',
      description: 'L\'écran tactile est défaillant',
      machine: 'HP Color LaserJet Pro MFP M479fdw',
      currentCounter: 18670,
      agent: 'Laila Mansouri',
      status: 'nouveau',
      createdAt: '2024-01-22',
      updatedAt: '2024-01-22'
    }
  ];

  const getStatusVariant = (status: Complaint['status']) => {
    switch (status) {
      case 'nouveau':
        return 'default';
      case 'en_cours':
        return 'secondary';
      case 'resolue':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: Complaint['status']) => {
    switch (status) {
      case 'nouveau':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'en_cours':
        return 'bg-orange-100 text-orange-800 hover:bg-orange-200';
      case 'resolue':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const getStatusLabel = (status: Complaint['status']) => {
    switch (status) {
      case 'nouveau':
        return 'Nouveau';
      case 'en_cours':
        return 'En cours de traitement';
      case 'resolue':
        return 'Résolue';
      default:
        return status;
    }
  };

  const filteredComplaints = useMemo(() => {
    return complaints.filter(complaint => {
      const matchesSearch = complaint.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          complaint.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          complaint.machine.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          complaint.agent.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || complaint.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [complaints, searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredComplaints.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedComplaints = filteredComplaints.slice(startIndex, startIndex + itemsPerPage);

  const handleExport = () => {
    const csvContent = [
      ['Référence', 'Titre', 'Machine', 'Compteur', 'Agent', 'Statut', 'Date création', 'Dernière mise à jour'].join(','),
      ...filteredComplaints.map(complaint => [
        complaint.id,
        `"${complaint.title}"`,
        `"${complaint.machine}"`,
        complaint.currentCounter,
        `"${complaint.agent}"`,
        getStatusLabel(complaint.status),
        complaint.createdAt,
        complaint.updatedAt
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'reclamations.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Mes Réclamations</CardTitle>
        <Button onClick={onCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle réclamation
        </Button>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par titre, machine, agent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="nouveau">Nouveau</SelectItem>
              <SelectItem value="en_cours">En cours de traitement</SelectItem>
              <SelectItem value="resolue">Résolue</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Référence</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Machine</TableHead>
                <TableHead>Compteur</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date création</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedComplaints.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                    Aucune réclamation trouvée
                  </TableCell>
                </TableRow>
              ) : (
                paginatedComplaints.map((complaint) => (
                  <TableRow key={complaint.id}>
                    <TableCell className="font-medium">{complaint.id}</TableCell>
                    <TableCell>{complaint.title}</TableCell>
                    <TableCell>{complaint.machine}</TableCell>
                    <TableCell>{complaint.currentCounter.toLocaleString()}</TableCell>
                    <TableCell>{complaint.agent}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={getStatusVariant(complaint.status)}
                        className={getStatusColor(complaint.status)}
                      >
                        {getStatusLabel(complaint.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(complaint.createdAt).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewDetail?.(complaint)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-2 py-4">
            <div className="text-sm text-muted-foreground">
              Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredComplaints.length)} sur {filteredComplaints.length} réclamations
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Précédent
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className="w-8 h-8 p-0"
                  >
                    {page}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplaintsList;